// LiveStream.js
import { connect } from 'react-redux';
import withRouter from '../component/WithRouter.jsx';
import { goBack } from 'redux-first-history';

import { DrmFeatureName } from '../resource/drmConstants.js';

import LiveStream from '../component/LiveStream.jsx';

import getOperationData from '../selector/getOperationData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getMeData from '../selector/getMeData.js';
import getStreamTokenDecodedData from '../selector/getStreamTokenDecodedData.js';
import getUserData from '../selector/getUserData.js';
import getPrivateShowStatus from '../selector/getPrivateShowStatus.js';
import getIsPrivateShowUnlocked from '../selector/getIsPrivateShowUnlocked.js';
import getMeBalance from '../selector/getMeBalance.js';
import getLiveStreamPrice from '../selector/getLiveStreamPrice.js';
import getIsRemoteConfigMerged from '../selector/getIsRemoteConfigMerged.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getDrmKeySystemAndRobustness from '../selector/getDrmKeySystemAndRobustness.js';
import getShouldShowLivestreamCta from '../selector/getShouldShowLivestreamCta.js';

import setLiveStreamPanel from '../action/setLiveStreamPanel.js';
import fetchUser from '../action/fetchUser.js';
import removeModals from '../action/removeModals';
import addModal from '../action/addModal.js';
import login from '../action/login.js';
import setPlayerData from '../action/setPlayerData.js';
import addvalue from '../action/addvalue.js';
import resetLiveStream from '../action/resetLiveStream.js';
import checkLivestreamChatPreflightAndOpenPanel from '../action/checkLivestreamChatPreflightAndOpenPanel.js';
import openLivestreamPopOutChat from '../action/openLivestreamPopOutChat.js';

import {
  PRESET_SD,
  SHOW_REMAINING_SECONDS_BALANCE_MULTIPLE,
} from '../resource/liveStreamConstants.js';

import {
  CONFIG_PRIORITY_CONFIGURE,
  CONFIG_PRIORITY_PRESENCE_USER,
  CONFIG_PRIORITY_PRESENCE_CLIENT,
} from '../resource/configPriority.js';

import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';

import {
  BLUROUT_PARAMETERS,
  LIVESTREAM_PAY_DURATION_SECONDS,
  LIVESTREAM_PUBLIC_SOURCE_FORMAT,
  LIVESTREAM_PRIVATE_SOURCE_FORMAT,
  LIVESTREAM_OPERATION_HINT,
  LIVESTREAM_EXIT_ALERT,
  FEATURE_GAMES,
} from '../RemoteConfigKeys.js';
import { breakpoint } from '../style/variables.js';

const mapStateToProps = (state, { userId, streamId }) => {
  const preset = getLiveStreamData(state, [streamId], 'preset');
  const meId = getMeData(state, 'id');
  const isStreamer = meId === streamId;
  const fundingId = getLiveStreamData(
    state,
    [streamId, 'privateShow'],
    'fundingId'
  );
  const streamTokenDecodedData = getStreamTokenDecodedData(state, streamId);
  const privateShowStatus = getPrivateShowStatus(state, streamId);

  const isPrivateShowUnlocked = getIsPrivateShowUnlocked(
    state,
    streamId,
    fundingId,
    meId
  );
  const isPrivateStreamUnlocked = getOperationData(
    state,
    ['liveStream', 'paid', streamId],
    'isPrivateStreamUnlocked'
  );
  const streamStatus = getLiveStreamData(state, [streamId], 'status');
  const balance = getMeBalance(state);
  const price = getLiveStreamPrice(state, streamId, PRESET_SD);
  const priceDuration =
    getRemoteConfigData(state, LIVESTREAM_PAY_DURATION_SECONDS) / 60;
  const tokenAvailableMin = streamTokenDecodedData?.exp
    ? Math.floor((streamTokenDecodedData?.exp - getCurrentUnixTimestamp()) / 60)
    : 0;

  const shouldDisplayBalanceWarning =
    preset === PRESET_SD &&
    !fundingId &&
    streamTokenDecodedData?.exp &&
    priceDuration === 1 &&
    balance < price * SHOW_REMAINING_SECONDS_BALANCE_MULTIPLE &&
    tokenAvailableMin < SHOW_REMAINING_SECONDS_BALANCE_MULTIPLE;

  const isAuthed = !!getMeData(state, 'id');

  const deviceWidth = getOperationData(state, ['device'], 'width');
  const isOnMobile = deviceWidth <= breakpoint.tablet;

  const isPrivateStreamSubscribed = getOperationData(
    state,
    ['pusher', 'channels'],
    `private-enc-stream@${streamId}`
  )?.subscribed;

  const { keySystem } = getDrmKeySystemAndRobustness(
    state,
    DrmFeatureName.LIVESTREAM
  );

  const previewBlurManifest = getRemoteConfigData(
    state,
    BLUROUT_PARAMETERS
  )?.[2];
  const isStreamPreviewBlurEnabled = !!previewBlurManifest;
  const shouldDisplayBlurCountdown =
    isStreamPreviewBlurEnabled &&
    streamStatus === PRESET_SD &&
    !streamTokenDecodedData;

  return {
    isAuthed,
    isStreamer,
    meId,
    userId,
    fundingId,
    drmTestStatus: getOperationData(state, ['drm'], 'testStatus'),
    privateShowStatus,
    streamStatus,
    isMuted: getOperationData(state, ['player'], 'isMuted'),
    isGiftPanelOpen: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isGiftPanelOpen'
    ),
    isChatPanelOpen: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isChatPanelOpen'
    ),
    isGamePanelOpen: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isGamePanelOpen'
    ),
    isTopLeaderboardScrolling: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isTopLeaderboardScrolling'
    ),
    isAsideExpanded: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isAsideExpanded'
    ),
    deviceHeight: getOperationData(state, ['device'], 'height'),
    preset,
    streamTokenDecodedData,
    username: getUserData(state, userId, 'username'),
    isRemoteConfigMerged: isAuthed
      ? getIsRemoteConfigMerged(state, CONFIG_PRIORITY_PRESENCE_USER)
      : getIsRemoteConfigMerged(state, CONFIG_PRIORITY_PRESENCE_CLIENT) ||
        getIsRemoteConfigMerged(state, CONFIG_PRIORITY_CONFIGURE),
    isLiveStreaming: getLiveStreamData(state, [streamId], 'isLiveStreaming'),
    isPrivateShowUnlocked,
    isPrivateStreamUnlocked,
    chatToastr: getOperationData(state, [streamId, 'chatToastr'], 'i18nKey'),
    shouldDisplayBalanceWarning,
    isPrivateStreamSubscribed,
    sourceId: getLiveStreamData(state, [streamId], 'sourceId'),
    sessionId: getLiveStreamData(state, [streamId], 'sessionId'),
    isOnMobile,
    deviceWidth,
    keySystem,
    streamPreviewBlurDuration: +getRemoteConfigData(
      state,
      BLUROUT_PARAMETERS
    )?.[1],
    publicSourceFormat: getRemoteConfigData(
      state,
      LIVESTREAM_PUBLIC_SOURCE_FORMAT
    ),
    privateSourceFormat: getRemoteConfigData(
      state,
      LIVESTREAM_PRIVATE_SOURCE_FORMAT
    ),
    shouldDisplayBlurCountdown,
    shouldShowLivestreamCta: getShouldShowLivestreamCta(state, streamId),
    hasScrollHintDisplayed: getMeData(state, 'hasScrollHintDisplayed'),
    shouldShowOperationHintUI:
      getRemoteConfigData(state, LIVESTREAM_OPERATION_HINT) === 1,
    shouldShowExitAlertModal:
      getRemoteConfigData(state, LIVESTREAM_EXIT_ALERT) === 1,
    games: getRemoteConfigData(state, FEATURE_GAMES),
  };
};

const mapDispatchToProps = (dispatch, { userId }) => {
  return {
    login: () => dispatch(login()),
    fetchUser: ({ userId }) => dispatch(fetchUser({ id: userId })),
    setGiftPanel: ({ isGiftPanelOpen }) =>
      dispatch(setLiveStreamPanel({ isGiftPanelOpen })),
    setChatPanel: ({ isChatPanelOpen }) =>
      dispatch(setLiveStreamPanel({ isChatPanelOpen })),
    setGamePanel: ({ isGamePanelOpen }) =>
      dispatch(setLiveStreamPanel({ isGamePanelOpen })),
    setMuted: data => dispatch(setPlayerData(data)),
    setAsideExpended: ({ isAsideExpanded }) =>
      dispatch(setLiveStreamPanel({ isAsideExpanded })),
    addvalue: () => dispatch(addvalue({ via: 'unlock-livestream' })),
    closeModal: ({ ids }) =>
      dispatch(
        removeModals({
          ids,
          transitionStatus: 'closing',
        })
      ),
    openModal: ({ id }) =>
      dispatch(
        addModal({ id, isHigherThanAll: true, transitionStatus: 'coming' })
      ),
    resetLiveStream: () =>
      dispatch(resetLiveStream({ streamId: userId, isLeaveLivestream: true })),
    checkLivestreamChatPreflightAndOpenPanel: ({ streamId }) =>
      dispatch(checkLivestreamChatPreflightAndOpenPanel({ streamId })),
    goBack: () => dispatch(goBack()),
    openLivestreamPopOutChat: ({ streamId }) =>
      dispatch(openLivestreamPopOutChat({ streamId })),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(LiveStream)
);
